"use client";

import { useState, useEffect, useRef } from "react";
import { Leaf, Sparkles, ArrowRightLeft, AlertCircle, Bot } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CustomTabs as Tabs, CustomTabsList as TabsList, CustomTabsTrigger as TabTrigger, CustomTabsContent as TabContent } from "@/components/ui/custom-tabs";
import { cn } from "@/lib/utils";
import { $fetch } from "@/lib/api";
import { marketDataWs } from "@/services/trade/market-data-ws";
import { marketService } from "@/services/trade/market-service";
import BalanceDisplay from "./balance-display";
import LimitOrderForm from "./limit-order-form";
import MarketOrderForm from "./market-order-form";
import StopOrderForm from "./stop-order-form";
import AutoTradingForm from "./auto-trading-form"
import { AutoTradingRedesigned } from "./auto-trading-redesigned";
import { AutoTradingEnhanced } from "./auto-trading-enhanced";
import { BacktestingPanel } from "./backtesting-panel";
import AiInvestmentForm from "../ai-investment";
import AgentTradingTab from "./agent-trading-tab";
import { NotificationProvider } from "@/components/ui/notification-provider";
import type { WalletData, TickerData } from "./types";
import { useTranslations } from "@/hooks/use-translations";

interface TradingFormPanelProps {
  symbol?: string;
  isEco?: boolean;
  onOrderSubmit?: (orderData: any) => Promise<any>;
}

export default function TradingFormPanel({
  symbol = "btc/usdt",
  isEco = false,
  onOrderSubmit,
}: TradingFormPanelProps) {
  const t = useTranslations("trade/components/trading/spot/index");
  const [buyMode, setBuyMode] = useState(true);
  const [orderType, setOrderType] = useState<"limit" | "market" | "stop" | "auto" | "backtest">(
    "limit"
  );
  const [tradingType, setTradingType] = useState<"trading" | "bridge" | "ai" | "agent">("trading");
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [isLoadingWallet, setIsLoadingWallet] = useState(false);
  const [tickerData, setTickerData] = useState<TickerData | null>(null);
  const [marketPrice, setMarketPrice] = useState("48,235.75");
  const [pricePrecision, setPricePrecision] = useState(2);
  const [amountPrecision, setAmountPrecision] = useState(4);
  const [minAmount, setMinAmount] = useState(0.0001);
  const [maxAmount, setMaxAmount] = useState(1000000);
  const [priceDirection, setPriceDirection] = useState<
    "up" | "down" | "neutral"
  >("neutral");
  const [lastPrice, setLastPrice] = useState<number | null>(null);
  const [currency, setCurrency] = useState("BTC");
  const [pair, setPair] = useState("USDT");

  // Add refs to prevent duplicate fetching
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Reset market price immediately when symbol changes
  useEffect(() => {
    // Reset price to loading state when symbol changes
    setMarketPrice("--");
    setLastPrice(null);
    setPriceDirection("neutral");
    setTickerData(null);

    // Clear session cache for the new symbol to force fresh data
    if (typeof window !== 'undefined') {
      const cacheKey = `trade_wallet_${symbol}`;
      sessionStorage.removeItem(cacheKey);
    }
  }, [symbol]);

  // Fetch market data on component mount
  useEffect(() => {
    try {
      // Use centralized market service to get market metadata
      const findMarketMetadata = async () => {
        try {
          const markets = await marketService.getSpotMarkets();
          const market = markets.find((m: any) => m.symbol === symbol);

          if (market) {
            const metadata = market.metadata;

            // Extract currency and pair from market data
            setCurrency(market.currency || "BTC");
            setPair(market.pair || "USDT");

            // Set precision based on market metadata
            if (metadata?.precision) {
              setPricePrecision(metadata.precision.price || 2);
              setAmountPrecision(metadata.precision.amount || 4);
            }

            // Set min/max amount based on market limits
            if (metadata?.limits?.amount) {
              setMinAmount(metadata.limits.amount.min || 0.0001);
              setMaxAmount(metadata.limits.amount.max || 1000000);
            }
          } else {
            // Fallback: extract from symbol if market not found
            // Handle slash format symbols
            if (symbol.includes("/")) {
              const [base, quote] = symbol.split("/");
              if (base && quote) {
                setCurrency(base.toUpperCase());
                setPair(quote.toUpperCase());
              } else {
                // Default fallback
                setCurrency("BTC");
                setPair("USDT");
              }
            } else if (symbol.endsWith("USDT")) {
              setCurrency(symbol.replace("USDT", ""));
              setPair("USDT");
            } else if (symbol.endsWith("BUSD")) {
              setCurrency(symbol.replace("BUSD", ""));
              setPair("BUSD");
            } else if (symbol.endsWith("USD")) {
              setCurrency(symbol.replace("USD", ""));
              setPair("USD");
            } else {
              // Default fallback
              setCurrency("BTC");
              setPair("USDT");
            }

            // Log warning for missing market data
            console.warn(`Market data not found for symbol: ${symbol}. Using fallback configuration.`);
          }
        } catch (error) {
          console.error("Error getting market metadata:", error);
          // Use default values if there's an error
          setCurrency("BTC");
          setPair("USDT");
          setPricePrecision(2);
          setAmountPrecision(4);
          setMinAmount(0.0001);
          setMaxAmount(1000000);
        }
      };

      findMarketMetadata();
    } catch (error) {
      console.error("Error parsing market data:", error);
      // Use default values if there's an error
      setCurrency("BTC");
      setPair("USDT");
      setPricePrecision(2);
      setAmountPrecision(4);
      setMinAmount(0.0001);
      setMaxAmount(1000000);
    }

    // Fetch wallet data
    fetchWalletData();
  }, [symbol]);

  // Refetch wallet data when currency or pair changes
  useEffect(() => {
    if (currency && pair) {
      fetchWalletData();
    }
  }, [currency, pair]);

  // Passkey wallet state for Bridge
  const [passkeySession, setPasskeySession] = useState<any>(null);
  const [otcBalance, setOtcBalance] = useState("0");
  const [otcxBalance, setOtcxBalance] = useState("0");
  const [usdoBalance, setUsdoBalance] = useState("0");
  const [passkeyAddress, setPasskeyAddress] = useState<string | null>(null);

  // Load passkey wallet address (display only — auth is via HttpOnly cookie)
  useEffect(() => {
    const addr = localStorage.getItem("otc_passkey_address");
    if (addr) {
      setPasskeyAddress(addr);
      setPasskeySession({ address: addr });
      // Fetch fresh balances from API
      fetchPasskeyBalances(addr);
    }
  }, []);

  // Fetch passkey wallet balances from API
  const fetchPasskeyBalances = async (address: string) => {
    try {
      // Fetch OTCX balance
      const { data: otcxData } = await $fetch({
        url: `/api/finance/wallet/SPOT/OTCX`,
        silentSuccess: true,
      });
      if (otcxData?.free) setOtcxBalance(otcxData.free);
      
      // Fetch USDO balance
      const { data: usdoData } = await $fetch({
        url: `/api/finance/wallet/SPOT/USDO`,
        silentSuccess: true,
      });
      if (usdoData?.free) setUsdoBalance(usdoData.free);
      
      // Fetch native OTC balance
      const { data: otcData } = await $fetch({
        url: `/api/finance/wallet/SPOT/OTC`,
        silentSuccess: true,
      });
      if (otcData?.free) setOtcBalance(otcData.free);
    } catch (e) {
      console.error("Failed to fetch passkey balances:", e);
    }
  };

  // Fetch wallet data for both currencies (only needs to be called once)
  const fetchWalletData = async () => {
    // Prevent duplicate calls within 1 second
    const now = Date.now();
    if (isFetchingRef.current || now - lastFetchTimeRef.current < 1000) {
      console.log(`[Trade] Wallet fetch throttled for ${symbol}`);
      return;
    }

    // Create cache key for this symbol
    const cacheKey = `trade_wallet_${currency}_${pair}`;

    // Check if we have recent cached data (within 30 seconds)
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data: cachedData, timestamp } = JSON.parse(cached);
        if (now - timestamp < 30000) { // 30 seconds cache
          console.log(`[Trade] Using cached wallet data for ${symbol}`);
          setWalletData({
            balance: cachedData.CURRENCY,
            availableBalance: cachedData.CURRENCY,
            currency: currency,
            currencyBalance: cachedData.CURRENCY,
            pairBalance: cachedData.PAIR,
          });
          return;
        }
      }
    }

    try {
      isFetchingRef.current = true;
      lastFetchTimeRef.current = now;
      setIsLoadingWallet(true);

      console.log(`[Trade] Fetching wallet data for ${symbol}`);
      // Use the symbol endpoint to fetch both currency and pair balances
      const endpoint = `/api/finance/wallet/symbol?type=SPOT&currency=${currency}&pair=${pair}`;

      const { data, error } = await $fetch({
        url: endpoint,
        silentSuccess: true,
      });

      if (!error && data) {
        // Cache the successful response
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data,
            timestamp: now
          }));
        }

        // Store both balances - the display logic will handle which one to show
        setWalletData({
          balance: data.CURRENCY, // Default to currency balance
          availableBalance: data.CURRENCY,
          currency: currency,
          currencyBalance: data.CURRENCY, // Store both balances for reference
          pairBalance: data.PAIR,
        });
      } else {
        console.error("Error fetching wallet data:", error);
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
    } finally {
      setIsLoadingWallet(false);
      isFetchingRef.current = false;
    }
  };

  // Subscribe to price updates
  useEffect(() => {
    if (!symbol) return;

    // Clean up any existing subscription first
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const handleTickerUpdate = (data: TickerData) => {
      setTickerData(data);

      // Format price according to precision
      const formattedPrice = data.last.toLocaleString(undefined, {
        minimumFractionDigits: pricePrecision,
        maximumFractionDigits: pricePrecision,
      });

      // Determine price direction
      if (lastPrice !== null) {
        if (data.last > lastPrice) {
          setPriceDirection("up");
        } else if (data.last < lastPrice) {
          setPriceDirection("down");
        }

        // Reset animation after 1 second
        const timeout = setTimeout(() => {
          setPriceDirection("neutral");
        }, 1000);

        return () => clearTimeout(timeout);
      }

      setLastPrice(data.last);
      setMarketPrice(formattedPrice);
    };

    // Subscribe to ticker updates with a small delay to ensure proper cleanup
    const subscriptionTimeout = setTimeout(() => {
      unsubscribeRef.current = marketDataWs.subscribe(
        {
          type: "ticker",
          symbol,
          marketType: isEco ? "eco" : "spot",
        },
        handleTickerUpdate
      );
    }, 50);

    return () => {
      clearTimeout(subscriptionTimeout);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [symbol, pricePrecision, lastPrice, isEco]);

  // Create wallet data structure from passkey balances for balance display
  const passkeyWalletData = {
    balance: parseFloat(otcBalance) || 0,
    availableBalance: parseFloat(otcBalance) || 0,
    currency: currency,
    currencyBalance: parseFloat(otcBalance) || 0, // Use OTC as primary currency
    pairBalance: parseFloat(usdoBalance) || 0, // Use USDO as pair
  };

  // Shared props for order forms
  const sharedProps = {
    symbol,
    currency,
    pair,
    buyMode,
    setBuyMode,
    marketPrice,
    pricePrecision,
    amountPrecision,
    minAmount,
    maxAmount,
    walletData: passkeyWalletData, // Use passkey wallet data instead
    priceDirection,
    onOrderSubmit,
    fetchWalletData,
    isEco,
  };

  return (
    <NotificationProvider position="top-right" maxVisible={3}>
      <div className="flex flex-col h-full bg-background dark:bg-black overflow-y-auto scrollbar-hide">
        {/* Market type indicator - only show for Eco markets */}
        {isEco && (
          <div className="px-3 py-1.5 bg-emerald-500/10 border-b border-emerald-500/20 flex items-center">
            <Leaf className="h-3.5 w-3.5 text-emerald-500 mr-1.5" />
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
              {t("eco_market")}
            </span>
            <Badge className="ml-auto bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-[10px]">
              {t("low_fee")}
            </Badge>
          </div>
        )}

        <div className="flex border-b border-border dark:border-zinc-800">
          <button
            onClick={() => setTradingType("trading")}
            className={cn(
              "flex items-center justify-center flex-1 py-2 text-xs font-medium",
              tradingType === "trading"
                ? "text-foreground dark:text-white border-b-2 border-primary dark:border-blue-500"
                : "text-muted-foreground dark:text-zinc-400"
            )}
          >
            {t("standard_trading")}
          </button>
          <button
            onClick={() => setTradingType("bridge")}
            className={cn(
              "flex items-center justify-center flex-1 py-2 text-xs font-medium",
              tradingType === "bridge"
                ? "text-foreground dark:text-white border-b-2 border-primary dark:border-blue-500"
                : "text-muted-foreground dark:text-zinc-400"
            )}
          >
            <ArrowRightLeft className="h-3 w-3 mr-1" />
            Bridge
          </button>
          <button
            onClick={() => setTradingType("ai")}
            className={cn(
              "flex items-center justify-center flex-1 py-2 text-xs font-medium",
              tradingType === "ai"
                ? "text-foreground dark:text-white border-b-2 border-primary dark:border-blue-500"
                : "text-muted-foreground dark:text-zinc-400"
            )}
          >
            <Sparkles className="h-3 w-3 mr-1" />
            AI
          </button>
          <button
            onClick={() => setTradingType("agent")}
            className={cn(
              "flex items-center justify-center flex-1 py-2 text-xs font-medium",
              tradingType === "agent"
                ? "text-foreground dark:text-white border-b-2 border-primary dark:border-blue-500"
                : "text-muted-foreground dark:text-zinc-400"
            )}
          >
            <Bot className="h-3 w-3 mr-1" />
            Agent
          </button>
        </div>

        {tradingType === "trading" && (
          <BalanceDisplay
            walletData={walletData}
            isLoadingWallet={isLoadingWallet}
            currency={currency}
            pair={pair}
            marketPrice={marketPrice}
          />
        )}

        {tradingType === "trading" ? (
          <Tabs
            defaultValue="limit"
            className="flex-1"
            value={orderType}
            onValueChange={(value) =>
              setOrderType(value as "limit" | "market" | "stop" | "auto" | "backtest")
            }
          >
            <TabsList className="w-full grid grid-cols-5 rounded-none">
              <TabTrigger value="limit" className="text-xs">{t("Limit")}</TabTrigger>
              <TabTrigger value="market" className="text-xs">{t("Market")}</TabTrigger>
              <TabTrigger value="stop" className="text-xs">{t("Stop")}</TabTrigger>
              <TabTrigger value="auto" className="text-xs">Auto</TabTrigger>
              <TabTrigger value="backtest" className="text-xs">Backtest</TabTrigger>
            </TabsList>

            <TabContent value="limit" className="p-2 space-y-2 min-h-[400px]">
              <LimitOrderForm {...sharedProps} />
            </TabContent>

            <TabContent value="market" className="p-2 min-h-[400px]">
              <MarketOrderForm {...sharedProps} />
            </TabContent>

            <TabContent value="stop" className="p-2 min-h-[400px]">
              <StopOrderForm {...sharedProps} />
            </TabContent>

            <TabContent value="auto" className="p-2 min-h-[400px]">
              <AutoTradingEnhanced symbol={symbol} />
            </TabContent>

            <TabContent value="backtest" className="p-2 min-h-[400px]">
              <BacktestingPanel symbol={symbol} />
            </TabContent>
          </Tabs>
        ) : tradingType === "bridge" ? (
          <BridgeTab
            passkeyAddress={passkeyAddress}
            otcBalance={otcBalance}
            otcxBalance={otcxBalance}
            usdoBalance={usdoBalance}
            onRefreshBalances={() => passkeyAddress && fetchPasskeyBalances(passkeyAddress)}
          />
        ) : tradingType === "agent" ? (
          <AgentTradingTab symbol={symbol} />
        ) : (
          <AiInvestmentForm isEco={isEco} symbol={symbol} />
        )}
      </div>
    </NotificationProvider>
  );
}

// Bridge Tab Component
function BridgeTab({ 
  passkeyAddress, 
  otcBalance, 
  otcxBalance, 
  usdoBalance,
  onRefreshBalances 
}: { 
  passkeyAddress: string | null;
  otcBalance: string;
  otcxBalance: string;
  usdoBalance: string;
  onRefreshBalances: () => void;
}) {
  const { toast } = useToast();
  const [amount, setAmount] = useState("");
  const [targetChain, setTargetChain] = useState("ethereum");
  const [recipient, setRecipient] = useState("");
  const [bridgeType, setBridgeType] = useState<"wormhole" | "axelar">("axelar");
  const [isLoading, setIsLoading] = useState(false);

  const chains = [
    { id: 'ethereum', name: 'Ethereum', icon: '🔷' },
    { id: 'bsc', name: 'BSC', icon: '🟡' },
    { id: 'polygon', name: 'Polygon', icon: '🟣' },
    { id: 'avalanche', name: 'Avalanche', icon: '🔺' },
    { id: 'base', name: 'Base', icon: '🔵' },
    { id: 'arbitrum', name: 'Arbitrum', icon: '🔵' },
    { id: 'optimism', name: 'Optimism', icon: '🔴' },
    { id: 'solana', name: 'Solana', icon: '☀️' },
  ];

  useEffect(() => {
    // Auto-set recipient to passkey address for EVM chains
    if (passkeyAddress && targetChain !== 'solana') {
      setRecipient(passkeyAddress);
    } else {
      setRecipient("");
    }
  }, [targetChain, passkeyAddress]);

  const handleBridge = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: "Invalid amount", variant: "destructive" });
      return;
    }
    if (!recipient) {
      toast({ title: "Recipient required", variant: "destructive" });
      return;
    }
    if (parseFloat(amount) > parseFloat(otcBalance)) {
      toast({ title: "Insufficient balance", variant: "destructive" });
      return;
    }

    setIsLoading(true);
    try {
      const credentialId = sessionStorage.getItem('passkey_credential_id') || 
                          localStorage.getItem('otc_credential_id');
      
      const response = await fetch("/api/ecosystem/bridge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourceChain: 'chain175',
          targetChain,
          amount: parseFloat(amount),
          recipient,
          bridgeType,
          credentialId,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast({ 
          title: "Bridge initiated!", 
          description: `Tx: ${result.data.txHash.slice(0, 10)}...` 
        });
        setAmount("");
        onRefreshBalances();
      } else {
        throw new Error(result.error || "Bridge failed");
      }
    } catch (error: any) {
      toast({ 
        title: "Bridge failed", 
        description: error.message,
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!passkeyAddress) {
    return (
      <div className="p-4 text-center">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          Connect your passkey wallet to use the bridge
        </p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      {/* Balances */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
          <p className="text-muted-foreground">OTCX</p>
          <p className="font-semibold">{parseFloat(otcxBalance).toFixed(4)}</p>
        </div>
        <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
          <p className="text-muted-foreground">USDO</p>
          <p className="font-semibold">{parseFloat(usdoBalance).toFixed(2)}</p>
        </div>
        <div className="p-2 rounded bg-gray-500/10 border border-gray-500/20">
          <p className="text-muted-foreground">OTC</p>
          <p className="font-semibold">{parseFloat(otcBalance).toFixed(4)}</p>
        </div>
      </div>

      {/* From */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">From</label>
        <div className="flex items-center gap-2 p-2 bg-muted rounded border">
          <span className="text-lg">⛽</span>
          <div>
            <p className="text-sm font-medium">OTC Network</p>
            <p className="text-xs text-muted-foreground">Chain 175</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-sm font-medium">{parseFloat(otcBalance).toFixed(4)} OTC</p>
            <p className="text-xs text-muted-foreground">Available</p>
          </div>
        </div>
      </div>

      {/* To */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">To</label>
        <select
          value={targetChain}
          onChange={(e) => setTargetChain(e.target.value)}
          className="w-full p-2 bg-muted rounded border text-sm"
        >
          {chains.map((chain) => (
            <option key={chain.id} value={chain.id}>
              {chain.icon} {chain.name}
            </option>
          ))}
        </select>
      </div>

      {/* Amount */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Amount (OTC)</label>
        <div className="relative">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full p-2 bg-muted rounded border text-sm"
          />
          <button
            onClick={() => setAmount(otcBalance)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-500"
          >
            MAX
          </button>
        </div>
      </div>

      {/* Recipient */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">
          Recipient Address ({targetChain === 'solana' ? 'Solana' : 'EVM'})
        </label>
        <input
          type="text"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder={targetChain === 'solana' ? 'Solana address...' : '0x...'}
          className="w-full p-2 bg-muted rounded border text-sm"
        />
      </div>

      {/* Bridge Type */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Bridge Type</label>
        <div className="flex gap-2">
          <button
            onClick={() => setBridgeType('axelar')}
            className={cn(
              "flex-1 py-1.5 px-2 rounded border text-xs",
              bridgeType === 'axelar'
                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                : 'bg-muted border-border'
            )}
          >
            Axelar
          </button>
          <button
            onClick={() => setBridgeType('wormhole')}
            className={cn(
              "flex-1 py-1.5 px-2 rounded border text-xs",
              bridgeType === 'wormhole'
                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                : 'bg-muted border-border'
            )}
          >
            Wormhole
          </button>
        </div>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded">
        <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-500/80">
          Bridge fees apply. Ensure the recipient address is correct for the selected chain.
        </p>
      </div>

      {/* Bridge Button */}
      <button
        onClick={handleBridge}
        disabled={isLoading || !amount || !recipient}
        className="w-full py-3 rounded bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold disabled:opacity-50"
      >
        {isLoading ? 'Bridging...' : 'Bridge OTC'}
      </button>
    </div>
  );
}
